// File: src/pages/AllProductPage.tsx

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShoppingCart } from 'lucide-react';

interface Product {
  id: number;
  slug: string;
  productName: string;
  description: string;
  price: number;
  discount?: number;
  imageUrl?: string;
  status: string;
}

// SERVER URL (enable these two lines when deploying):
const API_BASE_URL = 'https://www.ayushvatika.com/api';
const IMAGE_BASE_URL = 'https://www.ayushvatika.com/images/products/';
// LOCALHOST URL (currently enabled):
// const API_BASE_URL = 'http://localhost:8484/api';
// const IMAGE_BASE_URL = 'http://localhost:8484/images/products/';

const AllProductPage: React.FC = () => {
  const { parentSlug, childSlug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const pageTitle = childSlug 
    ? `Shop - Ayush Vatika` 
    : parentSlug
    ? `Shop - Ayush Vatika`
    : 'All Ayurvedic Products - Ayush Vatika';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API_BASE_URL}/products`;
        if (parentSlug && childSlug) {
           url = `${API_BASE_URL}/products/category/id/${childSlug}`;
        } else if (parentSlug) {
           url = `${API_BASE_URL}/products/category/id/${parentSlug}`;
        }
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) {
          console.error('Expected an array of products, received:', data);
          throw new Error('Products API did not return a product array.');
        }
        setProducts(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [parentSlug, childSlug]);

  const calculateFinalPrice = (price: number, discount?: number): number => {
    return (discount && discount > 0) ? price - (price * discount / 100) : price;
  };

  if (error) return <div className="text-center mt-20 text-red-500 font-bold tracking-tight">Error: {error}</div>;

  return (
    <div className="bg-[#fcfdfa] min-h-screen font-sans pb-20">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        
        {/* --- HEADER --- */}
        <div className="text-left mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#2e3d2f] uppercase tracking-wide">
               Explore <span className="text-green-700 underline decoration-green-700 decoration-[3px] underline-offset-[8px]">Our Collection</span>
            </h2>
        </div>

        {/* --- PRODUCT GRID --- */}
        {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[1,2,3].map(i => (
                <div key={i} className="h-96 bg-gray-100 rounded-2xl animate-pulse"></div>
            ))}
            </div>
        ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 bg-white rounded-2xl border border-gray-100">
                <ShoppingCart className="w-16 h-16 text-gray-200 mb-4" />
                <p className="text-gray-500 text-lg font-bold uppercase tracking-widest">No products found</p>
            </div>
        ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {products.map((product) => (
                <Link key={product.slug} to={`/product/${product.slug}`} className="group block h-full">
                
                <div className="bg-white rounded-2xl border border-gray-100 hover:border-green-600/30 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col overflow-hidden relative">
                    
                    {/* RED DISCOUNT TAG (Like Screenshot) */}
                    {product.discount && product.discount > 0 && product.status !== 'out of stock' && (
                      <div className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded shadow-md z-10">
                        {product.discount}% OFF
                      </div>
                    )}

                    {product.status === 'out of stock' && (
                      <span className="absolute top-4 right-4 bg-gray-800 text-white text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full z-10 shadow-sm">SOLD OUT</span>
                    )}

                    {/* --- IMAGE CONTAINER (FULL WIDTH - EDGE TO EDGE) --- */}
                    <div className="aspect-square w-full bg-gray-50 relative overflow-hidden">
                    {product.imageUrl ? (
                        <img
                        src={`${IMAGE_BASE_URL}${product.imageUrl}`}
                        alt={product.productName}
                        className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700 ease-in-out"
                        loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 font-bold uppercase tracking-widest text-xs">No Image</div>
                    )}
                    </div>

                    {/* --- CONTENT SECTION --- */}
                    <div className="p-6 flex flex-col flex-grow items-center text-center">
                        
                        {/* Title (Matched Font) */}
                        <h2 className="text-lg font-bold text-[#2e3d2f] line-clamp-2 mb-3 leading-snug group-hover:text-green-700 transition-colors">
                            {product.productName}
                        </h2>
                        
                        {/* Price Area */}
                        <div className="flex items-center justify-center gap-3 mb-6">
                            <span className="text-gray-400 line-through text-sm font-medium">₹{product.price.toFixed(0)}</span>
                            <span className="text-2xl font-black text-green-700">
                                ₹{calculateFinalPrice(product.price, product.discount).toFixed(0)}
                            </span>
                        </div>

                        {/* ADD TO BAG BUTTON (Exact Style) */}
                        <button
                            className={`w-full py-3.5 rounded-lg text-xs font-black transition-all uppercase tracking-[2px] mt-auto shadow-md
                            ${product.status === 'out of stock' 
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                                : 'bg-[#24422b] hover:bg-[#1a301f] text-white active:scale-95'}`}
                            disabled={product.status === 'out of stock'}
                        >
                            {product.status === 'out of stock' ? 'Out of Stock' : 'Add to Bag'}
                        </button>
                    </div>
                </div>
                </Link>
            ))}
            </div>
        )}
      </div>
    </div>
  );
};

export default AllProductPage;